import { useEffect, useState } from 'react';
import { Activity } from 'lucide-react';
import { api, type PublicMonitor } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { StatusDot } from '@/components/StatusDot';
import { UptimeBar } from '@/components/UptimeBar';
import { formatMs, formatRelative } from '@/lib/utils';

export function PublicStatus() {
  const [data, setData] = useState<{ updated_at: number; monitors: PublicMonitor[] } | null>(
    null
  );
  const [error, setError] = useState<string | null>(null);

  async function load() {
    try {
      setData(await api.publicStatus());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'load failed');
    }
  }

  useEffect(() => {
    load();
    const t = setInterval(load, 30_000);
    return () => clearInterval(t);
  }, []);

  const monitors = data?.monitors ?? [];
  const allUp =
    monitors.length > 0 &&
    monitors.every((m) => m.status === 'up' || m.status === 'maintenance');
  const anyDown = monitors.some((m) => m.status === 'down');
  const anyMaintenance = monitors.some((m) => m.status === 'maintenance');

  return (
    <div className="max-w-3xl mx-auto p-8 space-y-8">
      <header className="flex items-center gap-3">
        <Activity className="h-6 w-6 text-green-500" />
        <h1 className="text-2xl font-semibold tracking-tight">PX7 Status</h1>
        <a
          href="/admin"
          className="ml-auto text-sm text-muted-foreground hover:text-foreground underline-offset-4 hover:underline"
        >
          Admin →
        </a>
      </header>

      <Card className={anyDown ? 'border-destructive' : allUp ? 'border-green-500/40' : ''}>
        <CardHeader>
          <CardTitle className="text-xl">
            {anyDown
              ? 'Some services are experiencing issues'
              : allUp
                ? anyMaintenance
                  ? 'All systems operational (scheduled maintenance in progress)'
                  : 'All systems operational'
                : 'Status unknown'}
          </CardTitle>
        </CardHeader>
      </Card>

      {error && (
        <Card className="p-4 border-destructive/50 text-destructive">{error}</Card>
      )}

      <div className="space-y-3">
        {monitors.map((m) => (
          <Card key={m.id}>
            <CardContent className="pt-6 space-y-3">
              <div className="flex items-center gap-3">
                <StatusDot status={m.status} />
                <div className="flex-1">
                  <div className="font-medium">{m.name}</div>
                  <div className="text-xs text-muted-foreground">
                    Last check: {formatRelative(m.last_check_at)} ·{' '}
                    {formatMs(m.last_duration_ms)}
                  </div>
                </div>
                <div className="text-sm font-medium">
                  {m.status === 'up'
                    ? 'Operational'
                    : m.status === 'down'
                      ? 'Down'
                      : m.status === 'maintenance'
                        ? 'Maintenance'
                        : 'Unknown'}
                </div>
              </div>
              <UptimeBar
                buckets={m.uptime_hours.map((h) => ({
                  key: h.hour,
                  checks_total: h.checks_total,
                  checks_up: h.checks_up,
                }))}
                interval="hour"
                count={48}
              />
              {m.maintenance && (
                <div className="text-xs rounded border border-blue-500/50 bg-blue-500/10 text-blue-700 dark:text-blue-300 p-2">
                  Scheduled maintenance until {new Date(m.maintenance.ends_at * 1000).toLocaleString()}
                  {m.maintenance.reason ? ` — ${m.maintenance.reason}` : ''}
                </div>
              )}
              {m.open_incident && !m.maintenance && (
                <div className="text-xs rounded border border-destructive/50 bg-destructive/10 text-destructive p-2">
                  Open incident since {formatRelative(m.open_incident.opened_at * 1000)}:{' '}
                  {m.open_incident.last_error ?? 'investigating'}
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {data && (
        <footer className="text-center text-xs text-muted-foreground pt-4">
          Updated {formatRelative(data.updated_at * 1000)} · Created by PX7 Digital
        </footer>
      )}
    </div>
  );
}
